<template>
  <div
    class="inputDate"
    :class="[
      { focus: active },
      { disabled: disabled },
      'dev-edit-component',
    ]"
    :id="_componentUUID"
    @focusout="onFocusout"
    @focusin="onFocusin"
  >
    <div class="inputDate-input">
      <input
        ref="input"
        class="inputdate"
        type="text"
        autocomplete="off"
        :disabled="disabled"
        :name="name"
        v-model="text"
        :style="customStyle"
        @click="onInputTextClick"
        @keydown.delete="onKeydownDelete"
        @keydown.48="onKeydownNumber"
        @keydown.49="onKeydownNumber"
        @keydown.50="onKeydownNumber"
        @keydown.51="onKeydownNumber"
        @keydown.52="onKeydownNumber"
        @keydown.53="onKeydownNumber"
        @keydown.54="onKeydownNumber"
        @keydown.55="onKeydownNumber"
        @keydown.56="onKeydownNumber"
        @keydown.57="onKeydownNumber"
        @keydown.96="onKeydownNumber"
        @keydown.97="onKeydownNumber"
        @keydown.98="onKeydownNumber"
        @keydown.99="onKeydownNumber"
        @keydown.100="onKeydownNumber"
        @keydown.101="onKeydownNumber"
        @keydown.102="onKeydownNumber"
        @keydown.103="onKeydownNumber"
        @keydown.104="onKeydownNumber"
        @keydown.105="onKeydownNumber"
        @keydown.left="onKeydownArrow"
        @keydown.right="onKeydownArrow"
        @keydown="onKeydown"
      />
      <input type="hidden" :id="id" :value="resultValue" />
    </div>
    <div class="inputDate-calendar">
      <ejs-datepicker
        ref="datepicker"
        v-if="_usingDatepicker"
        :cssClass="`inputDate-${type ? `${type}` : 'grid'} dev-edit-component`"
        :disabled="disabled"
        :enabled="!disabled"
        :value="calendarValue"
        :start="depth"
        :depth="depth"
        @open="onDatepickerOpen"
        @close="onDatepickerClose"
        @change="onDatepickerChange"
        @renderDayCell="onRenderDayCell"
        @navigated="onNavigated"
      />
    </div>
    <div v-if="this.text !== '0000-00-00'" class="inputDate-clear">
      <button
        v-if="!required"
        type="button"
        :tabindex="-1"
        @click="onClearClick"
        :disabled="disabled"
      >
        Clear
      </button>
    </div>
  </div>
</template>

<style scoped>
.inputDate {box-sizing: border-box;position: relative;width: 100%;border: 1px solid #e0e0e0;border-radius: 3px;}
.inputDate .inputDate-calendar >>> .e-input-group {border: none !important;}
.inputDate .inputDate-calendar >>> .e-input-group.e-input-focus {border: none !important;}
.inputDate .inputDate-calendar >>> .e-input-group .e-input {visibility: hidden;}
.inputDate .inputDate-calendar >>> .e-input-group .e-clear-icon {visibility: hidden;}
.inputDate .inputDate-input {position: absolute;left: 0;top: 0;width: calc(100% - 38px);z-index: 10;}
.inputDate .inputDate-input input[type="text"] {display: block;box-sizing: border-box;width: 100%;padding: 0 6px;margin: 0;border: none;background-color: transparent;color: #666;font-size: 12px;line-height: 21px;font-family: "돋움", Dotum, Arial, Verdana, sans-serif;outline: none;}
.inputDate .inputDate-input input[type="text"]::placeholder {color: #bbb;}
.inputDate .inputDate-clear {position: absolute;right: 25px;top: 0;}
.inputDate .inputDate-clear button {overflow: hidden;width: 13px;height: 21px;padding: 0;border: none;background: transparent url("../../../assets/images/common/input-clear.png") no-repeat center center;text-indent: -1000px;cursor: pointer;outline: none;}
.inputDate.focus {border-color: #999;}
.inputDate.disabled {border-color: #e0e0e0;background-color: #f0f0f0;}
.inputDate.disabled .inputDate-input input[type="text"] {color: #bbb;cursor: default;}
.inputDate.disabled .inputDate-clear {display: none;opacity: 0.5;}
input.inputdate:focus {background-color: rgba(250, 244, 192, 0.2) !important;}
</style>

<script>
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { isDateFormatString } from "@/utils/string";
import {
  DATE_FORMAT_YYYY,
  DATE_FORMAT_YYYY_MM,
  DATE_FORMAT_YYYY_MM_DD,
  getCalendarInfoAndSetCalendarContentElementDayClassNew,
  getCurrentSelectedDateMomentInDateTimePicker,
  setIdInDatePickerSpan,
  setDateElementCssClassOnRenderCell,
  getTodayDate,
} from "@/utils/date";
import GolfErpAPI from "@/api/v2/GolfErpAPI";
import confirmDialogMixin from "@/views/layout/mixin/messagePopupDialogMixin";
import commonMixin from "@/views/layout/mixin/commonMixin";

const DATE_FORMATS = [
  DATE_FORMAT_YYYY_MM_DD,
  DATE_FORMAT_YYYY_MM,
  DATE_FORMAT_YYYY,
];

const EMPTY_OF_FORMAT = {
  "YYYY-MM-DD": "0000-00-00",
  "YYYY-MM": "0000-00",
  YYYY: "0000",
};

function replaceRange(target, string, startIndex, endIndex) {
  return `${target.substr(0, startIndex)}${string}${target.substr(endIndex)}`;
}

export default {
  name: "InputDate",
  mixins: [confirmDialogMixin, commonMixin],
  props: {
    id: String,
    name: {
      type: String,
      default: "name",
    },
    type: String,
    value: {
      type: String,
      validator(value) {
        return !value || isDateFormatString(value);
      },
    },
    depth: {
      type: String,
      default: () => null,
    },
    format: {
      type: String,
      validator(format) {
        return !(DATE_FORMATS.indexOf(format) < 0);
      },
      default: () => DATE_FORMAT_YYYY_MM_DD,
    },
    disabled: {
      type: [String, Boolean],
      validator(disabled) {
        return `${disabled}` === "true" || `${disabled}` === "false";
      },
      default: () => false,
    },
    usingDatepicker: {
      type: [String, Boolean],
      validator(disabled) {
        return `${disabled}` === "true" || `${disabled}` === "false";
      },
      default: () => true,
    },
    required: {
      type: Boolean,
      default: () => false,
    },
    notClear: {
      type: Boolean,
      default: () => false,
    },
    isGetDateInfo: {
      type: Boolean,
      default: () => false,
    },
    // 객실 관련 화면에서 사용 여부
    isUseRoom: {
      type: Boolean,
      default: () => false,
    },
    /**
     * true : 값이 없을 때 "" 를 binding
     * false : 값이 없을 때 undefined를 binding
     */
    isBindingEmptyString: {
      type: Boolean,
      default: () => false,
    },
    customStyle: {
      type: Object,
      default: () => {},
    },
    menuId: {
      type: Number,
      default: () => null,
    },
    isNotFutureDaySelect: {
      type: Boolean,
      default: () => false,
    },
  },
  created() {
    this.text = this.value || EMPTY_OF_FORMAT[this.format];
  },
  watch: {
    async value() {
      this.text = this.value || EMPTY_OF_FORMAT[this.format];
      if (this.isGetDateInfo) {
        const data =
          this.isUseRoom ?
            (await GolfErpAPI.getRoomCalendarByFromDateAndToDate({ fromDate: this.text, toDate: this.text }, true))[0] :
            (await GolfErpAPI.fetchCalenderInfo(this.text, true)).data;
        this.bsnCode = this.isUseRoom ? data.roomBsnCode : data.bsnCode;
        this.dwCode = data.dwCode;
      }
      this.$emit("change", {
        data: this.value,
        bsnCode: this.bsnCode,
        dwCode: this.dwCode,
      });
    },
    active(active) {
      if (!active) {
        this.renewalValueByEmitInput();
      }
    },
    text(newText, oldText) {
      if (/[ㄱ-ㅎㅏ-ㅣ|가-힣]/gi.test(newText)) {
        this.text = oldText;
      }
    },
  },
  data() {
    return {
      active: false,
      text: "",
      calendarListMap: new Map(),
      bsnCode: null,
      dwCode: null,
    };
  },
  computed: {
    _componentUUID() {
      return uuidv4();
    },
    _usingDatepicker() {
      return Boolean(this.usingDatepicker);
    },

    resultValue() {
      if (this.text === EMPTY_OF_FORMAT[this.format]) {
        return undefined;
      }

      return moment(this.text, this.format).isValid() ? this.text : this.value;
    },
    calendarValue() {
      const valueMoment = moment(this.value, this.format);
      const textMoment = moment(this.text, this.format);

      if (textMoment.isValid()) {
        return textMoment.toDate();
      } else if (textMoment.isValid()) {
        return valueMoment.toDate();
      } else {
        return null;
      }
    },
  },
  methods: {
    onFocusin() {
      this.active = true;
    },
    onFocusout(event) {
      if (!event.relatedTarget) {
        this.active = false;

        return;
      }

      if (
        this.containsDevEditComponentElement(
          event.relatedTarget,
          this._componentUUID
        )
      ) {
        return;
      }

      this.active = false;
    },
    onInputTextClick() {
      this.$nextTick(() => {
        this.$refs["input"].focus();
      });
    },
    onKeydownDelete(event) {
      event.preventDefault();

      const {
        keyCode,
        target: { selectionStart, selectionEnd },
      } = event;

      const deleteStartIndex =
        selectionStart === selectionEnd
          ? selectionStart + (keyCode === 8 ? -1 : 0)
          : selectionStart;
      const deleteEndIndex =
        selectionStart === selectionEnd
          ? selectionStart + (keyCode === 8 ? 0 : 1)
          : selectionEnd;

      const subString = event.target.value.substring(
        deleteStartIndex,
        deleteEndIndex
      );
      const maskString = subString.replace(/[0-9]/gi, "0");
      const result = replaceRange(
        event.target.value,
        maskString,
        deleteStartIndex,
        deleteEndIndex
      );

      this.text = result;
      this.$nextTick(() => {
        this.$refs["input"].setSelectionRange(
          deleteStartIndex,
          deleteStartIndex
        );
      });
    },
    onKeydownNumber(event) {
      event.preventDefault();

      const {
        key,
        target: { selectionStart },
      } = event;

      const keyValue = `${key}`;

      const deleteStartIndex = selectionStart;
      const deleteEndIndex = selectionStart + 1;
      const subString = event.target.value.substring(
        deleteStartIndex,
        deleteEndIndex
      );
      const maskString = subString.replace(/[0-9]/gi, keyValue);
      const result = replaceRange(
        event.target.value,
        maskString,
        deleteStartIndex,
        deleteEndIndex
      );

      this.text = result;
      let nextCursorIndex = selectionStart + 1;
      if (this.isHyphenIndex(nextCursorIndex)) {
        nextCursorIndex++;
      }

      this.$nextTick(() => {
        this.$refs["input"].setSelectionRange(nextCursorIndex, nextCursorIndex);
      });
    },
    onKeydownArrow(event) {
      event.preventDefault();

      const {
        key,
        target: { selectionStart, selectionEnd },
      } = event;

      let nextCursorIndex = selectionStart;

      if (key === "ArrowLeft") {
        if (selectionStart === selectionEnd) {
          nextCursorIndex = selectionStart - 1;
        } else {
          nextCursorIndex = selectionStart;
        }

        if (this.isHyphenIndex(nextCursorIndex)) {
          nextCursorIndex--;
        }
      } else if (key === "ArrowRight") {
        if (selectionStart === selectionEnd) {
          nextCursorIndex = selectionEnd + 1;
        } else {
          nextCursorIndex = selectionEnd;
        }

        if (this.isHyphenIndex(nextCursorIndex)) {
          nextCursorIndex++;
        }
      }

      this.$nextTick(() => {
        this.$refs["input"].setSelectionRange(nextCursorIndex, nextCursorIndex);
      });
    },
    onKeydown(event) {
      const { key } = event;

      if (key === "Tab") {
        return;
      }

      event.preventDefault();
    },
    onClearClick() {
      this.text = EMPTY_OF_FORMAT[this.format];
      this.$nextTick(() => {
        this.setSelectRangeFrontAndFocus();
      });
    },
    async onDatepickerOpen() {
      this.active = true;

      // map 초기화
      this.calendarListMap = new Map();

      // keyboard event 처리
      const datePickerInstance = this.$refs.datepicker.ej2Instances;
      datePickerInstance.contentElement.addEventListener("keyup", async () => {
        if (datePickerInstance.popupObj) {
          const focustDateMoment = getCurrentSelectedDateMomentInDateTimePicker(
            datePickerInstance
          );
          await getCalendarInfoAndSetCalendarContentElementDayClassNew(
            datePickerInstance.contentElement,
            focustDateMoment,
            this.calendarListMap
          );
        }
      });

      // calendar data loading & calendar dom setting
      let openDatePickerDate;
      if (this.value && this.value !== "0000-00-00") {
        openDatePickerDate = moment(this.value);
      } else {
        openDatePickerDate = moment();
      }
      if (openDatePickerDate.isValid()) {
        await getCalendarInfoAndSetCalendarContentElementDayClassNew(
          datePickerInstance.contentElement,
          openDatePickerDate,
          this.calendarListMap
        );
      }
    },
    onDatepickerClose() {
      this.active = false;

      this.renewalValueByEmitInput();
    },
    onDatepickerChange(args) {
      // 달력의 Today 클릭시 args.event가 null이라 반응이 없어 주석 처리함.
      // if (!args.event) {
      //   return;
      // }

      const valueMoment = moment(args.value);
      if (valueMoment.isValid()) {
        this.text = valueMoment.format(this.format);
        this.$nextTick(() => {
          this.$refs["input"].focus();
        });
      }
    },
    onTextFocusIn() {
      if (this.text) {
        this.$refs.input.setSelectionRange(0, this.text.length);
      }
    },
    setSelectRangeFrontAndFocus() {
      this.$refs["input"].setSelectionRange(0, 0);
      this.$refs["input"].focus();
    },
    isHyphenIndex(index) {
      return index === 4 || index === 7;
    },

    isDevEditComponentElement(element, id) {
      return element && element.dataset && element.dataset.id === id;
    },
    containsDevEditComponentElement(element, id) {
      if (this.isDevEditComponentElement(element, id)) {
        return true;
      } else {
        if (element.parentElement) {
          return this.containsDevEditComponentElement(
            element.parentElement,
            id
          );
        } else {
          return false;
        }
      }
    },
    onRenderDayCell(args) {
      const date = moment(args.date).format(DATE_FORMAT_YYYY_MM_DD);
      if (this.menuId && this.isNotFutureDaySelect) {
        if (this.commonMixinIsButtonDisableByMenuAuth("GET_AUTH", this.menuId)) {
          const todayDate = getTodayDate();
          if (date > todayDate) {
            args.isDisabled = true;
          }
        }
      }
      const spanElement = args.element.firstElementChild;

      // 모든 날짜 span element에 오늘 날짜로 ID 설정
      setIdInDatePickerSpan(date, spanElement);

      // calendarListMap 에 데이터가 있으면 표시해주기
      setDateElementCssClassOnRenderCell(
        args.date,
        spanElement.parentElement,
        this.calendarListMap
      );
    },
    async onNavigated(args) {
      if (args.view === "Month") {
        await getCalendarInfoAndSetCalendarContentElementDayClassNew(
          this.$refs.datepicker.ej2Instances.contentElement,
          moment(args.date),
          this.calendarListMap
        );
      }
    },
    // emit("input") 을 실행하여 value 갱신
    // focus out 될 때 / datepicker에서 날짜 선택 시 호출한다
    renewalValueByEmitInput() {
      if (!this.required && this.text === EMPTY_OF_FORMAT[this.format]) {
        if (this.notClear) {
          this.text = this.value || EMPTY_OF_FORMAT[this.format];
          this.$emit("input", this.value || EMPTY_OF_FORMAT[this.format]);
        } else {
          if (this.isBindingEmptyString) {
            this.$emit("input", "");
          } else {
            this.$emit("input", undefined);
          }
        }
      } else if (this.text !== this.value) {
        if (moment(this.text, this.format).isValid()) {
          if (this.menuId && this.isNotFutureDaySelect) {
            if (this.commonMixinIsButtonDisableByMenuAuth("GET_AUTH", this.menuId)) {
              const todayDate = getTodayDate();
              if (this.text > todayDate) {
                this.errorToast("미래일자 조회 불가능 합니다");
                this.text = this.value || EMPTY_OF_FORMAT[this.format];
                return;
              }
            }
          }
          this.$emit("input", this.text);
        } else {
          this.errorToast("Invalid Date");
          this.text = this.value || EMPTY_OF_FORMAT[this.format];
        }
      }
    },
    focus() {
      this.$refs["input"].focus();
      this.$refs["input"].select();
    },
  },
};
</script>
